/**
 * Fusion Mont delivery location bar.
 * Progressive enhancement: page works without geolocation.
 * Location label stored in localStorage only (not Shopify settings).
 *
 * Reverse geocode (street-level, no API key):
 * 1. Photon (OSM) — house number + road + locality
 * 2. Nominatim — fallback if Photon fails
 * 3. BigDataCloud — city/state/country last resort
 */
class FusionDeliveryBar extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.storageKey = this.dataset.storageKey || 'fusion-mont-delivery-location-v3';
    this.fallback = this.dataset.fallback || 'Add your delivery address';
    this.autoDetect = this.dataset.autoDetect === 'true';

    this.addressEl = this.querySelector('[data-fusion-delivery-address]');
    this.openBtn = this.querySelector('[data-fusion-delivery-open]');
    this.dialog = this.querySelector('[data-fusion-delivery-dialog]');
    this.form = this.querySelector('[data-fusion-delivery-form]');
    this.input = this.querySelector('[data-fusion-delivery-input]');
    this.detectBtn = this.querySelector('[data-fusion-delivery-detect]');
    this.statusEl = this.querySelector('[data-fusion-delivery-status]');
    this.gate = this.querySelector('[data-fusion-delivery-gate]');
    this.gateStatusEl = this.querySelector('[data-fusion-delivery-gate-status]');
    this.enableBtn = this.querySelector('[data-fusion-delivery-enable]');
    this.manualBtn = this.querySelector('[data-fusion-delivery-manual]');
    this.enableLabel = this.enableBtn?.textContent?.trim() || 'Enable location';

    this.openBtn?.addEventListener('click', () => this.openDialog());
    this.detectBtn?.addEventListener('click', () => this.detectLocation({ fromDialog: true }));
    this.enableBtn?.addEventListener('click', () => this.onEnableLocation());
    this.manualBtn?.addEventListener('click', () => this.onEnterManually());
    this.form?.addEventListener('submit', (event) => this.onFormSubmit(event));
    this.dialog?.addEventListener('close', () => this.onDialogClosed());

    if (this.isThemeEditor()) {
      this.closeGate();
      this.setAddress(this.fallback, { persist: false });
      return;
    }

    const saved = this.readStorage();
    if (saved?.label) {
      this.setAddress(saved.label, { persist: false });
      return;
    }

    this.setAddress(this.fallback, { persist: false });

    if (this.autoDetect) {
      this.startFirstVisitFlow();
    }
  }

  isThemeEditor() {
    return Boolean(
      window.Shopify?.designMode ||
        window.Shopify?.visualPreviewMode ||
        document.documentElement.classList.contains('shopify-design-mode')
    );
  }

  hasSavedAddress() {
    const label = this.readStorage()?.label;
    return Boolean(label && label !== this.fallback);
  }

  async startFirstVisitFlow() {
    const permission = await this.queryGeoPermission();
    if (permission === 'granted') {
      const ok = await this.detectLocation({ silent: true });
      if (ok) return;
    }
    this.openGate(permission === 'denied');
  }

  async queryGeoPermission() {
    try {
      if (!window.navigator?.permissions?.query) return 'prompt';
      const status = await window.navigator.permissions.query({ name: 'geolocation' });
      return status?.state || 'prompt';
    } catch (error) {
      return 'prompt';
    }
  }

  openGate(denied = false) {
    if (!this.gate) return;
    this.gate.hidden = false;
    document.documentElement.classList.add('fusion-delivery-gate-open');
    this.setGateStatus(
      denied
        ? 'Location is blocked in this browser. Enter an address, or allow location in site settings.'
        : ''
    );
    this.enableBtn?.focus();
  }

  closeGate() {
    if (!this.gate) return;
    this.gate.hidden = true;
    document.documentElement.classList.remove('fusion-delivery-gate-open');
    this.setGateStatus('');
    if (this.enableBtn) {
      this.enableBtn.disabled = false;
      this.enableBtn.textContent = this.enableLabel;
    }
  }

  setGateStatus(message) {
    if (!this.gateStatusEl) return;
    if (!message) {
      this.gateStatusEl.hidden = true;
      this.gateStatusEl.textContent = '';
      return;
    }
    this.gateStatusEl.hidden = false;
    this.gateStatusEl.textContent = message;
  }

  async onEnableLocation() {
    if (!this.enableBtn) return;
    if (this.isThemeEditor()) {
      this.setGateStatus('Location cannot run in the theme editor. Open the live store to allow GPS.');
      return;
    }
    this.enableBtn.disabled = true;
    this.enableBtn.textContent = 'Detecting location…';
    this.setGateStatus('Allow location in the browser popup.');
    const ok = await this.detectLocation({ fromGate: true });
    this.enableBtn.disabled = false;
    this.enableBtn.textContent = this.enableLabel;
    if (ok) {
      this.closeGate();
      this.closeDialog();
      return;
    }
    if (!this.gateStatusEl?.textContent) {
      this.setGateStatus(
        'Could not read location. Allow location for this site, or enter an address.'
      );
    }
  }

  onEnterManually() {
    this.closeGate();
    this.openDialog();
  }

  onDialogClosed() {
    if (this.isThemeEditor()) return;
    if (this.autoDetect && !this.hasSavedAddress()) {
      this.openGate(false);
    }
  }

  readStorage() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  writeStorage(payload) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (error) {
      /* ignore quota / private mode */
    }
  }

  setAddress(label, { persist = true, coords = null, parts = null } = {}) {
    if (!this.addressEl || !label) return;
    this.addressEl.textContent = label;
    this.addressEl.title = label;
    if (persist) {
      const parsed = parts || this.parseLabel(label);
      this.writeStorage({
        label,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
        address1: parsed.address1 || label,
        city: parsed.city || 'Bengaluru',
        province: parsed.province || 'Karnataka',
        zip: parsed.zip || '',
        country: parsed.country || 'India',
        updatedAt: Date.now(),
      });
    }
  }

  parseLabel(label) {
    const bits = String(label || '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    const zip = (String(label).match(/\b\d{6}\b/) || [])[0] || '';
    const cityBit = bits.find((bit) => /bengaluru|bangalore|mysuru|mysore|mumbai|chennai|hyderabad/i.test(bit));
    const city = cityBit || bits[bits.length - 1] || 'Bengaluru';
    const address1 = bits.slice(0, Math.max(1, bits.length - 1)).join(', ') || label;
    return { address1, city, province: 'Karnataka', zip, country: 'India' };
  }

  setStatus(message) {
    if (!this.statusEl) return;
    if (!message) {
      this.statusEl.hidden = true;
      this.statusEl.textContent = '';
      return;
    }
    this.statusEl.hidden = false;
    this.statusEl.textContent = message;
  }

  openDialog() {
    if (!this.dialog) return;
    if (this.input) {
      this.input.value = this.addressEl?.textContent?.trim() || '';
      if (this.input.value === this.fallback) this.input.value = '';
    }
    this.setStatus('');
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.input?.focus();
  }

  closeDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  }

  onFormSubmit(event) {
    const submitter = event.submitter;
    const value = submitter?.value || 'cancel';
    if (value === 'cancel') {
      this.setStatus('');
      return;
    }

    event.preventDefault();
    const label = this.input?.value?.trim();
    if (!label) {
      this.setStatus('Enter a delivery address.');
      this.input?.focus();
      return;
    }

    this.setAddress(label);
    this.setStatus('');
    this.closeDialog();
  }

  detectLocation({ fromDialog = false, silent = false, fromGate = false } = {}) {
    if (this.isThemeEditor()) {
      if (!silent) {
        const msg = 'Location cannot run in the theme editor. Test on the live store.';
        fromGate ? this.setGateStatus(msg) : this.setStatus(msg);
      }
      return Promise.resolve(false);
    }

    if (!window.isSecureContext) {
      if (!silent) {
        const msg = 'Location needs a secure (https) connection.';
        fromGate ? this.setGateStatus(msg) : this.setStatus(msg);
      }
      return Promise.resolve(false);
    }

    if (!window.navigator?.geolocation) {
      if (!silent) {
        const msg = 'Location is not available in this browser.';
        fromGate ? this.setGateStatus(msg) : this.setStatus(msg);
      }
      if (!fromDialog && !fromGate) this.setAddress(this.fallback, { persist: false });
      return Promise.resolve(false);
    }

    if (fromDialog) this.setStatus('Detecting location…');

    return new Promise((resolve) => {
      window.navigator.geolocation.getCurrentPosition(
        (position) => {
          this.onGeoSuccess(position, { fromDialog, silent, fromGate })
            .then(() => resolve(true))
            .catch(() => {
              this.onGeoError({ fromDialog, silent, fromGate, error: { code: 2 } });
              resolve(false);
            });
        },
        (error) => {
          this.onGeoError({ fromDialog, silent, fromGate, error });
          resolve(false);
        },
        { enableHighAccuracy: false, timeout: 12000, maximumAge: 120000 }
      );
    });
  }

  async onGeoSuccess(position, { fromDialog, silent, fromGate }) {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    let label = 'Location detected';
    let parts = null;

    try {
      const geo = await this.reverseGeocode(lat, lng);
      if (geo && typeof geo === 'object') {
        label = geo.label || label;
        parts = geo;
      } else if (geo) {
        label = geo;
      }
    } catch (error) {
      /* keep generic label */
    }

    if (parts) {
      parts = await this.fillPostcode(lat, lng, parts);
      if (parts.zip && label && !label.includes(parts.zip)) {
        label = `${label}, ${parts.zip}`;
        parts.label = label;
      }
    }

    this.setAddress(label, { coords: { lat, lng }, parts });
    if (this.input) this.input.value = label;
    if (fromDialog) {
      this.setStatus('Location updated. Save to confirm, or edit the address.');
    }
  }

  onGeoError({ fromDialog, silent, fromGate, error }) {
    if (silent) return;
    let message = 'Could not access location. Enter an address instead.';
    if (error?.code === 1) {
      message = 'Location is blocked. Allow it in the browser address bar, then try again.';
    } else if (error?.code === 3) {
      message = 'Location timed out. Try again, or enter an address.';
    }
    fromGate ? this.setGateStatus(message) : this.setStatus(message);
    if (!fromDialog && !fromGate && !this.hasSavedAddress()) {
      this.setAddress(this.fallback, { persist: false });
    }
  }

  async reverseGeocode(lat, lng) {
    const photon = await this.fetchJson(
      'https://photon.komoot.io/reverse' +
        `?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`
    );
    const photonParts = this.partsFromPhoton(photon);
    if (photonParts?.label) return photonParts;

    const arcgis = await this.fetchJson(
      'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode' +
        `?f=json&langCode=en&location=${encodeURIComponent(lng)},${encodeURIComponent(lat)}`
    );
    const arcgisParts = this.partsFromArcgis(arcgis);
    if (arcgisParts?.label) return arcgisParts;

    const nominatim = await this.fetchJson(
      'https://nominatim.openstreetmap.org/reverse' +
        `?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}` +
        '&format=jsonv2&addressdetails=1&zoom=18'
    );
    const nominatimParts = this.partsFromNominatim(nominatim);
    if (nominatimParts?.label) return nominatimParts;

    const fallback = await this.fetchJson(
      'https://api.bigdatacloud.net/data/reverse-geocode-client' +
        `?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}&localityLanguage=en`
    );
    const label = this.formatCityFallback(fallback);
    return label
      ? { label, ...this.parseLabel(label), zip: this.extractPin(fallback?.postcode, label) }
      : null;
  }

  extractPin(...values) {
    for (let i = 0; i < values.length; i++) {
      const match = String(values[i] || '').match(/\b[1-9][0-9]{5}\b/);
      if (match) return match[0];
    }
    return '';
  }

  async fillPostcode(lat, lng, parts) {
    const next = Object.assign({}, parts);
    next.zip = this.extractPin(next.zip, next.label, next.address1);
    if (next.zip) return next;

    const nominatim = await this.fetchJson(
      'https://nominatim.openstreetmap.org/reverse' +
        `?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}` +
        '&format=jsonv2&addressdetails=1&zoom=16'
    );
    next.zip = this.extractPin(nominatim?.address?.postcode, nominatim?.display_name);
    if (next.zip) return next;

    const arcgis = await this.fetchJson(
      'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode' +
        `?f=json&langCode=en&location=${encodeURIComponent(lng)},${encodeURIComponent(lat)}`
    );
    next.zip = this.extractPin(arcgis?.address?.Postal, arcgis?.address?.Match_addr);
    if (next.zip) return next;

    const bdc = await this.fetchJson(
      'https://api.bigdatacloud.net/data/reverse-geocode-client' +
        `?latitude=${encodeURIComponent(lat)}&longitude=${encodeURIComponent(lng)}&localityLanguage=en`
    );
    next.zip = this.extractPin(bdc?.postcode, JSON.stringify(bdc?.localityInfo || {}));
    return next;
  }

  partsFromArcgis(data) {
    const addr = data?.address;
    if (!addr) return null;
    const address1 = addr.Address || addr.Match_addr || '';
    const label =
      this.uniqueParts([address1, addr.Neighborhood, addr.City || addr.District]).join(', ') ||
      addr.Match_addr ||
      null;
    if (!label) return null;
    return {
      label,
      address1: address1 || label,
      city: addr.City || addr.District || this.parseLabel(label).city,
      province: addr.Region || addr.Subregion || 'Karnataka',
      zip: this.extractPin(addr.Postal, addr.Match_addr, label),
      country: 'India',
    };
  }

  partsFromPhoton(data) {
    const label = this.formatPhoton(data);
    if (!label) return null;
    const props = data?.features?.[0]?.properties || {};
    const address1 = [props.housenumber, props.street || props.name].filter(Boolean).join(', ') || label;
    return {
      label,
      address1,
      city: props.city || props.locality || props.county || this.parseLabel(label).city,
      province: props.state || 'Karnataka',
      zip: this.extractPin(props.postcode, label),
      country: 'India',
    };
  }

  partsFromNominatim(data) {
    const label = this.formatNominatim(data);
    if (!label) return null;
    const addr = data?.address || {};
    const address1 =
      [addr.house_number, addr.road || addr.residential || addr.pedestrian].filter(Boolean).join(', ') ||
      label;
    return {
      label,
      address1,
      city: addr.city || addr.town || addr.village || this.parseLabel(label).city,
      province: addr.state || 'Karnataka',
      zip: this.extractPin(addr.postcode, data?.display_name, label),
      country: 'India',
    };
  }

  async fetchJson(url) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  }

  uniqueParts(parts) {
    const seen = new Set();
    const out = [];
    parts.forEach((part) => {
      const value = String(part || '').trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(value);
    });
    return out;
  }

  formatPhoton(data) {
    const props = data?.features?.[0]?.properties;
    if (!props) return null;
    const line = [props.housenumber, props.street || props.name].filter(Boolean).join(', ');
    return (
      this.uniqueParts([
        line,
        props.locality,
        props.district,
        props.county,
        props.city,
      ]).join(', ') || null
    );
  }

  formatNominatim(data) {
    const addr = data?.address;
    if (!addr) return data?.display_name || null;
    const line = [addr.house_number, addr.road || addr.residential || addr.pedestrian]
      .filter(Boolean)
      .join(', ');
    return (
      this.uniqueParts([
        line,
        addr.neighbourhood,
        addr.quarter,
        addr.suburb,
        addr.city_district,
        addr.village,
        addr.town,
        addr.city,
      ]).join(', ') || data.display_name || null
    );
  }

  formatCityFallback(data) {
    if (!data) return null;
    const parts = this.uniqueParts([
      data.locality || data.city,
      data.principalSubdivision,
      data.countryName,
    ]);
    return parts.length ? parts.join(', ') : data.localityInfo?.informative?.[0]?.name || null;
  }
}

if (!customElements.get('fusion-delivery-bar')) {
  customElements.define('fusion-delivery-bar', FusionDeliveryBar);
}
